import { Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { Input } from "@outschool/ui-legacy-component-library";
import React, { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce/lib";

import { useUpdateChildMutation } from "../../hooks/Learners";
import { LearnerFormWarning } from "./LearnerFormWarning";

type LearnerNameWarningProps = {
  name: string;
};

export const LearnerNameRegex = new RegExp(
  /(^[A-Za-z]{2,})([ ]{1,})([A-Za-z]{1,})/
);

export function LearnerNameWarning({ name }: LearnerNameWarningProps) {
  const { t } = useTranslation(
    "client\\components\\learner\\LearnerNameWarning"
  );
  const [showNameWarning, setShowNameWarning] = useState(false);

  useEffect(() => {
    setShowNameWarning(LearnerNameRegex.test(name));
  }, [name]);

  if (!showNameWarning) {
    return null;
  }

  return (
    <LearnerFormWarning
      message={t(
        "To protect your learner’s privacy, enter their first name only."
      )}
    />
  );
}

type UpdateLeanderNameInputFieldProps = {
  learnerUid: string;
  learnerName: string;
  onUpdateLearnerName: (name: string) => void;
} & React.ComponentProps<typeof Input>;
export function UpdateLeanderNameInputField({
  learnerUid,
  learnerName,
  onUpdateLearnerName,
  ...inputProps
}: UpdateLeanderNameInputFieldProps) {
  const { t } = useTranslation(
    "client\\components\\learner\\LearnerNameWarning"
  );

  const [currentLearnerName, setCurrentLearnerName] = useState(learnerName);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const { updateChild, data, loading, error } = useUpdateChildMutation();

  const [debouncedUpdateChild] = useDebouncedCallback(({ name, uid }) => {
    updateChild({ name, uid });
  }, 300);

  useEffect(() => {
    if (data) {
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      onUpdateLearnerName(currentLearnerName);
    }
  }, [data, currentLearnerName, onUpdateLearnerName]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.currentTarget.value;
    debouncedUpdateChild({ name, uid: learnerUid });
    setCurrentLearnerName(name);
  };

  return (
    <>
      <Input
        type="text"
        value={currentLearnerName}
        data-private
        disabled={false}
        placeholder={t("Your learner’s first name")}
        onChange={handleNameChange}
        {...inputProps}
      />
      {showSuccessMessage && (
        <Typography variant="body2" sx={{ color: "main.main" }}>
          {t("Name updated successfully")}
        </Typography>
      )}
      {loading && (
        <Typography variant="body2" sx={{ color: "info.main" }}>
          {t("Updating name...")}
        </Typography>
      )}
      {error && (
        <Typography variant="body2" sx={{ color: "error.500" }}>
          {t("An error occurred while updating the name")}
        </Typography>
      )}

      <LearnerNameWarning name={currentLearnerName} />
    </>
  );
}
